import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import DeferredAttachmentSection from "../../components/DeferredAttachmentSection";
import logo from "../../images/logo.png";

const FuelCard = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [expiryDate, setExpiryDate] = useState("");
  const [fcNumber, setFcNumber] = useState("");
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // 🔍 Fetch Fuel Card
  const fetchFuelCard = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/fuelcard/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setFcNumber(res.data.fc_number || "");
      setExpiryDate(
        res.data.fc_expiry_date
          ? new Date(res.data.fc_expiry_date).toISOString().split("T")[0]
          : ""
      );
      setUploadedFiles(res.data.files || []);
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchFuelCard();
  }, [fetchFuelCard]);

  // 🔍 Signature Status
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 44;
        const status =
          res.data.status?.[String(TASK_ID)] ??
          res.data.status?.fuel_card ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleSelectFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    setFiles((prev) => {
      const existingKeys = new Set(
        prev.map((file) => `${file.name}_${file.size}_${file.lastModified}`)
      );
      const next = [...prev];

      selected.forEach((file) => {
        const key = `${file.name}_${file.size}_${file.lastModified}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push(file);
        }
      });

      return next;
    });

    e.target.value = "";
  };

  const removePendingFile = (fileToRemove) => {
    setFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          )
      )
    );
  };

  const markFileForDelete = (file) => {
    const confirmed = window.confirm(
      `Delete uploaded file "${file.original_name}"?`
    );
    if (!confirmed) return;

    setFilesToDelete((prev) => [...prev, file.id]);
    setUploadedFiles((prev) => prev.filter((f) => f.id !== file.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("fc_expiry_date", expiryDate);
    formData.append("fc_number", fcNumber);
    formData.append("filesToDelete", JSON.stringify(filesToDelete));

    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      setIsSaving(true);
      const response = await axios.post(
        `${API_BASE}/api/fuelcard/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert(response.data.message || "Saved successfully!");

      setFiles([]);
      setFilesToDelete([]);

      fetchFuelCard();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Fuel Card</span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Fuel Card</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label>Fuel Card Number *</label>
              <input
                type="text"
                className="form-control"
                value={fcNumber}
                onChange={(e) => setFcNumber(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-4 mb-3">
              <label>Expiry Date *</label>
              <input
                type="date"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-12">
              <DeferredAttachmentSection
                label="Select File (PDF / JPG / PNG)"
                disabled={(!isAdmin && isSigned) || isSaving}
                pendingFiles={files}
                uploadedFiles={uploadedFiles}
                onSelectFiles={handleSelectFiles}
                onRemovePendingFile={removePendingFile}
                onDeleteUploadedFile={markFileForDelete}
                getUploadedFileKey={(file) => file.id}
                getUploadedFileName={(file) => file.original_name}
                getUploadedFileUrl={(file) =>
                  `${API_BASE}/uploads/fuelcards/${file.path}`
                }
                getUploadedFileMeta={(file) =>
                  `Uploaded on: ${new Date(file.uploaded_at).toLocaleString()}`
                }
              />
            </div>
          </div>

          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(!isAdmin && isSigned) || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>

        <hr className="my-5" />

        <SignatureManager
          taskName="fuel_card"
          taskId={44}
          userId={userId}
          isAdmin={isAdmin}
        />

        <ReviewManager
          taskName="fuel_card"
          taskId={44}
          userId={userId}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
};

export default FuelCard;
